import EventEmitter from "eventemitter3";
import {
  ILayoutManager,
  IGameConfig,
  ILayoutInfo,
  ILayoutLegacyInfo,
  ILegacyGameConfig,
  ICanvasSize
} from "./interface";
import * as utils from "./utils";
import { getDeviceType, isIOSDevice, isSafariBrowser } from "./device";
export class LayoutManager extends EventEmitter implements ILayoutManager {
  constructor(private gameConfig: IGameConfig | ILegacyGameConfig) {
    super();
    const isIOS = isIOSDevice(window);
    const bodyElement = document.body;

    if (this.gameConfig.isLegacy) {
      bodyElement.classList.add("ratio-16x9");
    }

    utils.addOSSpecificClass(bodyElement, isIOS);

    window.requestAnimationFrame(() => this.onResize());
    window.addEventListener("load", this.onResize.bind(this));
    window.addEventListener("resize", this.onResize.bind(this));
    document.addEventListener("gesturestart", e => e.preventDefault(), false);
  }

  private onResize() {
    const lmInfo = this.gameConfig.isLegacy ? this.getInfo() : this.getLayoutData();

    this.emit("resize", lmInfo);
    this.updateLayoutContent(lmInfo);
  }

  public updateLayoutContent(
    lmInfo = this.gameConfig.isLegacy ? this.getInfo() : this.getLayoutData()
  ): void {
    const { gameViewportSize, viewportSize, platform } = lmInfo;
    const isPortrait = utils.isPortrait(viewportSize, this.gameConfig.keypadType, this.gameConfig.isLegacy, platform);
    const isCustomCanvasSizeAvailable = (lmInfo as ILayoutLegacyInfo).isCustomCanvasSizeAvailable; 

    const bodyElement = document.body;
    const docElement = document.documentElement;

    utils.addPropertiesToDocument(docElement, viewportSize, gameViewportSize);
    utils.addPlatformClass(docElement, platform);
    utils.addClassToBody(bodyElement, isPortrait, platform, isCustomCanvasSizeAvailable);
    utils.addAlignmentClass(bodyElement, viewportSize.width, gameViewportSize.width, isPortrait);

    // FIXME: this is to window scroll after address bar in mobile landscape(safari)
    if (
      lmInfo.platform === utils.Device.MOBILE
    ) {
      window.scrollTo(0, 0);
    }
  }

  public getInfo(): ILayoutLegacyInfo {
    const platform = getDeviceType(window);
    const isSafari = isSafariBrowser(window);
    const safeAreaBottom = utils.getSafeArea(window, "bottom");
    const viewportSize = utils.getViewPortDimensions(
      document.documentElement,
      window,
      platform,
      safeAreaBottom,
      isSafari
    );

    const isPortrait = utils.isPortrait(viewportSize, this.gameConfig.keypadType, this.gameConfig.isLegacy, platform);
    const orientation = utils.getOrientation(isPortrait);

    const canvasSize = this.gameConfig.canvasSize as ICanvasSize;
    const platformResolution = utils.getCanvasSize(canvasSize, platform, orientation);

    const isCustomCanvasSizeAvailable = utils.isCustomCanvasSizeAvailable(canvasSize, orientation, platform);

    const baseResolution = utils.getBaseResolution(
      canvasSize,
      isPortrait,
      isCustomCanvasSizeAvailable,
      platform
    );
    const scaleFactor = utils.getScaleFactor(platformResolution, baseResolution);

    const gameViewportSize = utils.get16x9Dimensions(
      viewportSize,
      orientation,
      isCustomCanvasSizeAvailable,
      platform
    );

    const scale = utils.getScale(gameViewportSize, baseResolution);

    return {
      scale,
      platform,
      baseResolution,
      platformResolution,
      scaleFactor,
      orientation,
      viewportSize,
      gameViewportSize,
      isCustomCanvasSizeAvailable
    };
  }

  // Need to re visit this method. If we want to keep one api for both new and legacy game.
  // what data is required for new games ??
  public getLayoutData(): ILayoutInfo {
    const gameConfig = this.gameConfig as IGameConfig;
    const gameViewportSize = utils.getGameWrapperSize(gameConfig.gameContainerElement);

    const platform = getDeviceType(window);
    const isSafari = isSafariBrowser(window);
    const safeAreaBottom = utils.getSafeArea(window, "bottom");
    const viewportSize = utils.getViewPortDimensions(
      document.documentElement,
      window,
      platform,
      safeAreaBottom,
      isSafari
    );

    const isPortrait = utils.isPortrait(viewportSize, this.gameConfig.keypadType, this.gameConfig.isLegacy, platform);
    const orientation = utils.getOrientation(isPortrait);

    return {
      platform,
      orientation,
      viewportSize,
      gameViewportSize
    };
  }
}
